import styled from 'styled-components';
import { useState } from 'react';
import { Controller, Control } from 'react-hook-form';
import { EmailInput, ErrorText } from '@buoyhealth/common.buoy-input';
import TermsAndConditions from './TermsAndConditions';
import { trackFormStart } from './analytics';

export interface IForm {
  email: string;
  termsAgreed: boolean;
  newsletterAgreed: boolean;
}

export interface IEmailTranscriptFormProps {
  control: Control<IForm, any>;
  /** Whether or not error should be shown on submit */
  showError: boolean;
  /** Whether or not a generic error should be shown on failed submit */
  showGenericError: boolean;
  /** Form type for analytics purposes*/
  formType: 'eoi-embedded' | 'popup';
  /** Interview token */
  interview: string;
}

const Container = styled.div<{ error: boolean }>`
  font-size: 14px;
  // Explicitly set error font size to 14px
  .Mui-error {
    font-size: 14px;
  }
  .MuiInputBase-root {
    background-color: white;
  }

  .MuiGrid-item {
    border-radius: 4px;
    background-color: ${({ theme }) => theme.palette.blue[0]};
    padding: 12px;
  }

  .Buoy__EmailTranscript__Dialog__TC .MuiGrid-item {
    border: ${({ error }) => (error ? '1px solid #d32f2f' : '0px solid transparent')};
    margin-top: 16px;
    margin-bottom: ${({ error }) => (error ? '0' : '8px')};
  }

  .Buoy__EmailTranscript__Dialog__Newsletter .MuiGrid-item {
    margin-top: ${({ error }) => (error ? '8px' : '0')};
  }
`;

const Form = ({
  control,
  formType,
  interview,
  showError,
  showGenericError,
}: IEmailTranscriptFormProps) => {
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsModified, setTermsModified] = useState(false);
  // Show error on submit if terms not agreed
  // Also show error if modifying terms and not agreed
  const shouldShowTermsError = (showError && !termsAgreed) || (!termsAgreed && termsModified);
  return (
    <Container error={shouldShowTermsError} className="Buoy__EmailTranscript__Form">
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState }) => {
          const { ref: inputRef, ...fieldProps } = field;
          return (
            <EmailInput
              error={!!fieldState.error}
              fieldProps={fieldProps}
              inputRef={inputRef}
              onChange={(event) => {
                if (!fieldState.isDirty) {
                  trackFormStart(interview, formType);
                }
                field.onChange(event);
              }}
            />
          );
        }}
        rules={{ required: true, pattern: /\S+@\S+\.\S+/ }}
      />
      <Controller
        name="termsAgreed"
        control={control}
        render={({ field, fieldState }) => (
          <div className="Buoy__EmailTranscript__Dialog__TC">
            <TermsAndConditions
              termsCopy={'I agree to'}
              termsAgreed={field.value}
              onChange={(event) => {
                field.onChange(event);
                setTermsAgreed(event.target.checked);
                setTermsModified(fieldState.isDirty);
              }}
            />
          </div>
        )}
      />
      <ErrorText show={shouldShowTermsError} message={'Required'} />
      <Controller
        name="newsletterAgreed"
        control={control}
        render={({ field }) => (
          <div className="Buoy__EmailTranscript__Dialog__Newsletter">
            <TermsAndConditions
              consentCopy={
                'I would also like to receive relevant healthcare news and offer emails from Buoy (optional)'
              }
              termsAgreed={field.value}
              onChange={(event) => field.onChange(event)}
            />
          </div>
        )}
      />
      <ErrorText
        show={showGenericError}
        message={'An error occurred while submitting. Please try again.'}
      />
    </Container>
  );
};

export default Form;
